import { isArray, isEmpty } from "lodash";
import store from "../../configureStore";
import { RENDER_MESSAGE_TYPE_FORM2 } from "../../constants";
import { MessageTypeConstants } from "../../../services/bot/Message";
import { showSnackbarV2 } from "../../actions/notification";


export const handleMessageTypeForm2 = (conversationId, message) => (dispatch) => {
	const tabStack = store.getState().tabStack || {};
	const hasExistingTabs = !isEmpty(tabStack);

	const updatedData = hasExistingTabs
		? getUpdatedFormTabData(tabStack[conversationId], message)
		: createInitialFormTabData(message);

	dispatch({
		type: RENDER_MESSAGE_TYPE_FORM2,
		data: {
			[conversationId]: updatedData,
		},
	});
};

const getUpdatedFormTabData = (tabData, message) => {
	return message?.options?.action
		? applyFormAction(tabData, message)
		: updateFormTabWithoutAction(tabData, message);
};


const createInitialFormTabData = (message) => {
	const showTab = !message?.options?.updateInBackground;

	return {
		...(showTab && { selectedTab: message?.options?.tabId }),
		components: [message],
	};
};


const applyFormAction = (tabData, message) => {
	const action = message?.options?.action;

	switch (action) {
		case "change":
			return handleFormChangeAction(tabData, message, true);
		case "validation":
		case "results":
			return handleFormResultAction(tabData, message);
		default:
			return tabData;
	}
};

const updateFormTabWithoutAction = (tabData, message) => {
	const hasComponents = tabData && !isEmpty(tabData.components);

	let updatedData = hasComponents
		? updateFormComponents(tabData, message)
		: initializeFormComponents(tabData, message);

	const showTab = !message?.options?.updateInBackground;
	const isNotModal = !message?.options?.modal;

	if (showTab && isNotModal) {
		updatedData = {
			...updatedData,
			selectedTab: message?.options?.tabId,
		};
	}

	return updatedData;
};


const updateFormComponents = (tabData, message) => {
	const tabIds = tabData.components.map(item => item.options.tabId);
	const exists = tabIds.includes(message.options.tabId);

	// Clone and optionally modify the message
	const updatedMessage = message.options?.modal
		? { ...message, hidden: true }
		: message;

	let updatedComponents;

	if (!exists) {
		updatedComponents = [...tabData.components, updatedMessage];
	} else if (message.options?.modal) {
		updatedComponents = [...tabData.components, updatedMessage];
	} else {
		updatedComponents = tabData.components.map(item =>
			item.options.tabId === message.options.tabId ? updatedMessage : item
		);
	}

	return {
		...tabData,
		components: updatedComponents,
	};
};


const initializeFormComponents = (tabData, message) => ({
	...tabData,
	components: [message],
});


export const handleFormChangeAction = (tabData, message, promptOnClose) => {
	const hasComponents = !isEmpty(tabData?.components);

	if (hasComponents) {
		const handler = message?.options?.parent
			? handleChangeActionWithParent
			: handleChangeActionWithoutParent;

		return handler(tabData, message, promptOnClose);
	}

	return tabData;
};


export const handleFormResultAction = (tabData, message, promptOnClose) => {
	const hasComponents = !isEmpty(tabData?.components);

	if (hasComponents) {
		const handler = message?.options?.parent
			? handleResultActionWithParent
			: handleResultActionWithoutParent;

		return handler(tabData, message, promptOnClose);
	}

	return tabData;
};

// Centralized promptOnClose merge utility
const mergePromptOnClose = (options, promptOnClose) => ({
	...options,
	promptOnClose: promptOnClose !== undefined ? promptOnClose : options?.promptOnClose,
});

export const handleChangeActionWithParent = (tabStack, message, promptOnClose) => {
	const updatedComponents = tabStack.components.map((item) => {
		if (!item?.options?.controlId || !message?.options?.controlId || item?.options?.controlId !== message?.options?.parent) {
			return item;
		}

		const updatedMessage = item.message.map((msg) => {
			if (msg?.message?.options?.controlId !== message?.options?.controlId) {
				return msg;
			}

			let previousFields = [];
			if (Array.isArray(msg?.message?.fields)) {
				previousFields = msg.message.fields;
			} else if (
				typeof msg?.message?.fields === 'object' &&
				msg?.message?.fields?.fields
			) {
				previousFields = msg.message.fields.fields;
			}

			let messageFields = [];
			if (Array.isArray(message?.message?.update)) {
				messageFields = message.message.update;
			} else if (Array.isArray(message?.message?.fields)) {
				messageFields = message.message.fields;
			}
			// else if (Array.isArray(message?.message?.results)) {
			// 	messageFields = message?.message?.results
			// }

			const updatedFields = previousFields.map((field) => {
				for (let i in messageFields) {
					if (messageFields[i].id === field.id) {
						return {
							...messageFields[i],
							results: msg.message.results || messageFields[i].results,
							validationResult: message.message.validationResult,
							validationMessage: message.message.validationMessage,
						};
					}
				}
				if (field.id !== message.message.field) {
					return field;
				}

				return {
					...field,
					results: message.message.results,
					validationResult: message.message.validationResult,
					validationMessage: message.message.validationMessage,
				};
			});

			const existingIds = new Set(previousFields.map((f) => f.id));
			const newFields = messageFields.filter((msgField) => !existingIds.has(msgField.id));

			const finalFields = [...updatedFields, ...newFields];
			if (typeof msg?.message?.fields === 'object' &&
				msg?.message?.fields?.fields) {
				const isOptions = message?.options ?? {};

				return {
					...msg,
					message: {
						...msg.message,
						fields: {
							...msg.message.fields,
							fields: finalFields,
						},
						options: (msg.message.options && isOptions ? message?.options : msg.message.options),
					},
				};
			}
			// Add new fields from messageFields that don’t exist in previousFields


			return {
				...msg,
				message: {
					...msg.message,
					fields: finalFields,
				},
			};
		});

		return {
			...item,
			message: updatedMessage,
			options: {
				...item.options,
				promptOnClose: promptOnClose !== undefined ? promptOnClose : item.options.promptOnClose,
			},
		};
	});

	return {
		...tabStack,
		components: updatedComponents,
	};
};


export const handleChangeActionWithoutParent = (tabData, message, promptOnClose) => {
	const updatedComponents = tabData.components.map((component) => {
		if (!component?.options?.tabId || !message?.options?.tabId || component?.options?.tabId !== message?.options?.tabId) {
			return component;
		}

		if (component.messageType !== MessageTypeConstants.MESSAGE_TYPE_FORM2) {
			return { ...component, options: mergePromptOnClose(component.options, promptOnClose) };
		}

		let newFields = message?.message?.update || message?.message?.fields || [];

		const isArrayMessage = Array.isArray(component.message);
		const originalFields = isArrayMessage ? component.message : component.message?.fields || [];
		const updatedFields = originalFields.map((field) => {
			const newField = newFields.find((newField) => newField.id === field.id);
			if (!newField) return field;
			return { ...field, ...newField };
		});

		if (message?.options && message.options?.isSearchedFrontedKey) {
			component.options.isSearchedFrontedKey = true
		}
		
		return {
			...component,
			message: isArrayMessage ? updatedFields : { ...component.message, fields: updatedFields },
			options: mergePromptOnClose(component.options, promptOnClose),
		};
	});

	return { ...tabData, components: updatedComponents };
};


export const handleResultActionWithParent = (tabData, message, promptOnClose) => {
	const updatedComponents = tabData.components.map((component) => {
		if (!component?.options?.controlId || !message?.options?.controlId || component?.options?.controlId !== message?.options?.parent) {
			return component;
		}

		const updatedMessage = component.message.map((msg) => {
			if (msg?.message?.options?.controlId !== message?.options?.controlId) return msg;

			let updatedFields
			if (msg.message && isArray(msg.message.fields)) {
				updatedFields = msg.message.fields.map((field) => {
					const targetFieldId = message?.message?.field?.field || message.message.field;

					if (field.id !== targetFieldId) {
						return field;
					}
					return {
						...field,
						results: message.message.results,
						validationResult: message.message.validationResult,
						validationMessage: message.message.validationMessage,
					};
				});
			}
			else if (msg.message.fields?.fields && isArray(msg.message.fields.fields)) {
				const updatedFieldList = msg.message.fields.fields.map((field) => {
					if (
						field.id !== message.message.field &&
						field.id !== message?.message?.field?.field
					) {
						return field;
					}

					return {
						...field,
						results: message.message.results,
						validationResult: message.message.validationResult,
						validationMessage: message.message.validationMessage,
					};
				});
				// Preserve the entire `fields` object structure
				updatedFields = {
					...msg.message.fields, // preserves `collectionData`
					fields: updatedFieldList, // only `fields` array is updated
				};
			}

			return { ...msg, message: { ...msg.message, fields: updatedFields } };
		});

		return {
			...component,
			message: updatedMessage,
			options: mergePromptOnClose(component.options, promptOnClose),
		};
	});

	return {
		...tabData,
		components: updatedComponents,
	};
};


export const handleResultActionWithoutParent = (tabData, message, promptOnClose) => {
	const updatedComponents = tabData.components.map((component) => {
		if (!component?.options?.controlId || !message?.options?.controlId || component?.options?.controlId !== message?.options?.controlId) {
			return component;
		}

		if (component.messageType !== MessageTypeConstants.MESSAGE_TYPE_FORM2) {
			return { ...component, options: mergePromptOnClose(component.options, promptOnClose) };
		}

		const isArrayMessage = Array.isArray(component.message);
		const originalFields = isArrayMessage ? component.message : component.message?.fields || [];

		let updatedFields = [...originalFields];
		let updatedGenericError = component.genericError;
		if (message.message.field) {
			updatedFields = updatedFields.map((field) => {
				if (field.id !== message.message.field) {
					return field;
				}
				// let existingResults = field?.results || [];
				let newResults = message.message.results || [];

				// // Combine and dedupe by id
				// const mergedResults = [
				// 	...existingResults,
				// 	...newResults.filter(
				// 		nr => !existingResults.some(er => er.id === nr.id)
				// 	)
				// ];

				return {
					...field,
					results: newResults,
					validationResult: message.message.validationResult,
					validationMessage: message.message.validationMessage,
				};

			});
		} else {
			updatedGenericError = message.message;
			if (!message.message.validationResult) {
				store.dispatch(
					showSnackbarV2("error", message.message.validationMessage)
				);
			}
		}

		return {
			...component,
			message: isArrayMessage ? updatedFields : { ...component.message, fields: updatedFields },
			genericError: updatedGenericError,
			options: mergePromptOnClose(component.options, promptOnClose),
		};
	});

	return { ...tabData, components: updatedComponents };
};